import mammoth from 'mammoth';
import Papa from 'papaparse';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BiDownload,
  BiRedo,
  BiRotateRight,
  BiUndo,
  BiX,
  BiZoomIn,
  BiZoomOut,
} from 'react-icons/bi';
import { Document, Page, pdfjs, type PageProps } from 'react-pdf';
import * as XLSX from 'xlsx';

import type { MediaStatus } from '@/common/media';
import { useAuthorizedFileUrl } from '@/hooks/useAuthorizedFileUrl';
import { useBackupVideo } from '@/hooks/useBackupVideo';
import { usePinchZoom } from '@/hooks/usePinchZoom';
import { useStreamingFileUrl } from '@/hooks/useStreamingFileUrl';
import { formatSize } from '@/pages/Documents/documents.utils';
import { downloadFromLink } from '@/utils/download-file-from-link';
import { isStreamableFileLink } from '@/utils/file-links';
import { toast } from '@/utils/toast';

import LoaderSpinner from '../LoaderSpinner/LoaderSpinner';

import s from './DocumentPreview.module.scss';
import { fileExtension } from './previewable';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export const DocumentPreviewModal = ({
  document,
  onClose,
}: {
  document: {
    contentType: string;
    name: string;
    storageLink: string;
    /** Bytes. Rendered as "Size" only when present. */
    size?: number;
    /** ISO string. Rendered as "Uploaded" only when present. */
    uploadedAt?: string;
    /** Rendered as "Filed in" only when present. */
    folderName?: string;
    /**
     * Says "processing" from the first frame rather than after the first refused
     * request. Optional: the preview finds out on its own and plays the file as
     * soon as it is ready either way.
     */
    mediaStatus?: MediaStatus | null;
    /**
     * A video's `backup` rendition (`backupLink(variants)`), played instead when
     * this browser cannot play the main one. Loaded the same way as the main file.
     */
    backupLink?: null | string;
  };
  onClose(): void;
}) => {
  const { contentType, name, storageLink, size, uploadedAt, folderName, mediaStatus } = document;
  const isVideo = contentType.startsWith('video');
  const rendition = useBackupVideo(storageLink, {
    backup: isVideo ? document.backupLink : null,
    contentType,
  });
  const source = rendition.link ?? storageLink;
  // A chat or gallery video streams itself, range by range; any other video arrives whole
  // and can run to 100 MB — too big for the default request timeout.
  const streams = isVideo && isStreamableFileLink(source);
  const file = useAuthorizedFileUrl(source, { enabled: !streams, longTimeout: isVideo });
  const stream = useStreamingFileUrl(source, {
    enabled: streams,
    pending: mediaStatus === 'pending',
    onUnplayable: rendition.onUnplayable,
  });
  const url = streams ? stream.src : file.url;
  const error = streams ? stream.error : file.error;
  const processing = streams ? stream.processing : file.processing;

  let content = null;
  if (processing) {
    content = (
      <div className={s.content} style={{ padding: 16 }}>
        <LoaderSpinner
          fullHeight={false}
          label={`${isVideo ? 'Video' : 'File'} is almost ready.`}
        />
      </div>
    );
  } else if (error) {
    content = <p className={s.notSupported}>Couldn’t load the file. Please try again.</p>;
  } else if (!url) {
    content = (
      <div className={s.content} style={{ padding: 16 }}>
        Loading…
      </div>
    );
  } else if (contentType.startsWith('image')) {
    content = <ImageViewer name={name} storageLink={url} />;
  } else if (isVideo) {
    content = (
      <div className={s.viewer}>
        <video
          className={s.videoPlayer}
          src={url}
          controls
          autoPlay
          playsInline
          title={name}
          onError={streams ? stream.onError : rendition.onError}
          onLoadedMetadata={(event) => {
            if (!rendition.onLoadedMetadata(event) && streams) stream.onLoadedMetadata(event);
          }}
        />
      </div>
    );
  } else if (contentType === 'application/pdf') {
    content = <PDFViewer storageLink={url} />;
  } else if (contentType === 'text/csv') {
    content = <CsvViewer url={url} />;
  } else if (contentType === 'text/plain') {
    content = <PlainTextViewer url={url} />;
  } else if (
    contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    content = <DocxViewer url={url} />;
  } else if (
    contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    contentType === 'application/vnd.ms-excel'
  ) {
    content = <XlsxViewer url={url} />;
  } else {
    const extension = fileExtension(name);
    const typeLabel = extension ? `${extension} document` : contentType || 'File';
    content = (
      <div className={s.notSupported}>
        <div className={s.canvas}>
          <span className={s.canvasBadge} aria-hidden="true">
            {extension ?? 'FILE'}
          </span>
          <p className={s.canvasNote}>
            {extension
              ? `There’s no preview for ${extension} files. Please download the file to open it on your device.`
              : `There’s no preview for this file. Please download it to open it on your device.`}
          </p>
          <button
            type="button"
            className={s.canvasDownload}
            onClick={() => {
              downloadFromLink(storageLink, name).catch((err) => {
                console.error(err);
                toast.error('Couldn’t download the file. Please try again.');
              });
            }}
          >
            <BiDownload aria-hidden="true" /> Download
          </button>
        </div>
        <dl className={s.metaGrid}>
          <div className={s.metaRow}>
            <dt>Type</dt>
            <dd>{typeLabel}</dd>
          </div>
          {size !== undefined && (
            <div className={s.metaRow}>
              <dt>Size</dt>
              <dd>{formatSize(size)}</dd>
            </div>
          )}
          {uploadedAt && (
            <div className={s.metaRow}>
              <dt>Uploaded</dt>
              <dd>
                {new Date(uploadedAt).toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </dd>
            </div>
          )}
          {folderName && (
            <div className={s.metaRow}>
              <dt>Filed in</dt>
              <dd>{folderName}</dd>
            </div>
          )}
        </dl>
        <div className={s.actions}>
          <button type="button" className={s.closeAction} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return createPortal(
    <div className={s.PreviewWrapper} onClick={onClose}>
      <article className={s.Preview} onClick={(e) => e.stopPropagation()}>
        <div className={s.title}>
          <h6>{name}</h6>
          <button className={s.close} onClick={onClose}>
            <BiX size={24} />
          </button>
        </div>
        {content}
      </article>
    </div>,
    window.document.body,
  );
};

const DocxViewer = ({ url }: { url: string }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { liveScale, zoomIn, zoomOut, pinchRef } = usePinchZoom<HTMLDivElement>({
    min: 0.5,
    max: 3.0,
  });

  useEffect(() => {
    fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buffer) => mammoth.convertToHtml({ arrayBuffer: buffer }))
      .then((result) => setHtml(result.value))
      .catch((err) => setError(err.message ?? 'Couldn’t load the document. Please try again.'));
  }, [url]);

  if (error) {
    return (
      <div className={s.content} style={{ padding: 16, color: 'red' }}>
        {error}
      </div>
    );
  }

  if (!html) {
    return (
      <div className={s.content} style={{ padding: 16 }}>
        Loading…
      </div>
    );
  }

  return (
    <div className={s.docxwrapper}>
      <div className={s.ScalingViewerControls}>
        <div />
        <div>
          <button onClick={zoomOut} className={s.ZoomButton}>
            <BiZoomOut />
          </button>
          <span className="text-sm font-medium min-w-16 text-center">
            {Math.round(liveScale * 100)}%
          </span>
          <button onClick={zoomIn} className={s.ZoomButton}>
            <BiZoomIn />
          </button>
        </div>
      </div>
      <div className={s.content} ref={pinchRef}>
        <div
          className={s.docx}
          style={{ transform: `scale(${liveScale})`, transformOrigin: 'top left' }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
};

// keep in sync with `.PreviewWrapper` horizontal padding (16px each side)
const PREVIEW_HORIZONTAL_PADDING = 32;

const getAvailableWidth = () => window.innerWidth - PREVIEW_HORIZONTAL_PADDING;

/** width a viewer can use before it starts overflowing the preview */
const useAvailableWidth = () => {
  const [availableWidth, setAvailableWidth] = useState(getAvailableWidth);

  useEffect(() => {
    const handleResize = () => setAvailableWidth(getAvailableWidth());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return availableWidth;
};

// 64px top + 16px bottom wrapper padding + ~49px title bar + ~56px controls row
const PREVIEW_VERTICAL_OVERHEAD = 185;
const getAvailableHeight = () => Math.max(window.innerHeight - PREVIEW_VERTICAL_OVERHEAD, 100);

/** height a viewer can use before it starts overflowing the preview */
const useAvailableHeight = () => {
  const [availableHeight, setAvailableHeight] = useState(getAvailableHeight);
  useEffect(() => {
    const update = () => setAvailableHeight(getAvailableHeight());
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return availableHeight;
};

const ImageViewer = ({ name, storageLink }: { name: string; storageLink: string }) => {
  const { liveScale, zoomIn, zoomOut, pinchRef } = usePinchZoom<HTMLDivElement>({
    min: 0.15,
    max: 6.0,
  });
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const availableWidth = useAvailableWidth();
  const availableHeight = useAvailableHeight();

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setNaturalSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
  };

  // 100% zoom = contain-fit (both axes), matching the PDF viewer's width-axis semantics
  const { w: naturalWidth, h: naturalHeight } = naturalSize;
  const baseWidth = (() => {
    if (!naturalWidth || !naturalHeight) return 0;
    const byWidth = Math.min(naturalWidth, availableWidth);
    const heightAtByWidth = (byWidth * naturalHeight) / naturalWidth;
    return heightAtByWidth <= availableHeight
      ? byWidth
      : (availableHeight * naturalWidth) / naturalHeight;
  })();
  const scaledWidth = baseWidth ? baseWidth * liveScale : undefined;

  return (
    <div className={s.viewer}>
      <div className={s.ScalingViewerControls}>
        <div></div>
        <div>
          <button onClick={zoomOut} className={s.ZoomButton}>
            <BiZoomOut />
          </button>
          <span className="text-sm font-medium min-w-16 text-center">
            {Math.round(liveScale * 100)}%
          </span>
          <button onClick={zoomIn} className={s.ZoomButton}>
            <BiZoomIn />
          </button>
        </div>
      </div>
      <div className={s.content} ref={pinchRef}>
        <img
          onLoad={handleLoad}
          alt={name}
          src={storageLink}
          width={scaledWidth}
          style={{ maxWidth: baseWidth ? 'none' : '100%' }}
        />
      </div>
    </div>
  );
};

const CsvViewer = ({ url }: { url: string }) => {
  const [data, setData] = useState<Record<string, string>[]>([]);
  const [columns, setColumns] = useState<string[]>([]);

  useEffect(() => {
    fetch(url)
      .then((res) => res.text())
      .then((text) => {
        const result = Papa.parse(text, { header: true, skipEmptyLines: true });
        setData(result.data as Record<string, string>[]);
        setColumns(result.meta.fields ?? []);
      })
      .catch(console.error);
  }, [url]);

  return <TableViewer data={data} columns={columns} />;
};

const PlainTextViewer = ({ url }: { url: string }) => {
  const [data, setData] = useState('');

  useEffect(() => {
    fetch(url)
      .then((res) => res.text())
      .then((result) => setData(result))
      .catch(console.error);
  }, [url]);

  return <div className={s.plainTxt}>{data}</div>;
};

const TableViewer = ({ data, columns }: { data: Record<string, string>[]; columns: string[] }) => (
  <div className={s.content}>
    <table className={s.csv}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr key={i}>
            {columns.map((c) => (
              <td key={c}>{row[c]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const XlsxViewer = ({ url }: { url: string }) => {
  const [data, setData] = useState<Record<string, string>[]>([]);
  const [columns, setColumns] = useState<string[]>([]);

  useEffect(() => {
    fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, defval: '' });

        if (rows.length === 0) return;

        const maxCols = Math.max(...rows.map((r) => r.length));
        const cols = Array.from({ length: maxCols }, (_, i) =>
          i < 26 ? String.fromCharCode(65 + i) : `A${String.fromCharCode(65 + i - 26)}`,
        );
        setColumns(cols);
        setData(rows.map((row) => Object.fromEntries(cols.map((col, i) => [col, row[i] ?? '']))));
      })
      .catch(console.error);
  }, [url]);

  return <TableViewer data={data} columns={columns} />;
};

function PDFViewer({ storageLink }: { storageLink: string }) {
  const [numPages, setNumPages] = useState<null | number>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null);
  const availableWidth = useAvailableWidth();
  const { scale, previewScale, liveScale, zoomIn, zoomOut, pinchRef } =
    usePinchZoom<HTMLDivElement>({ min: 0.5, max: 3.0 });

  function onDocumentLoadSuccess({ numPages }: { numPages: number | null }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  // fires again whenever the render scale changes, so keep the object stable when nothing moved
  const onPageLoadSuccess: NonNullable<PageProps['onLoadSuccess']> = (page) => {
    const { width, height } = page.getViewport({ scale: 1, rotation: 0 });
    setPageSize((prev) =>
      prev?.width === width && prev?.height === height ? prev : { width, height },
    );
  };

  function changePage(offset: number) {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  function rotate() {
    setRotation((prevRotation) => (prevRotation + 90) % 360);
  }

  // at 100% an oversized page fits the preview width; smaller pages keep their own size.
  // react-pdf renders at `scale * (width / viewportWidth)`, so zoom still multiplies this
  const upright = rotation % 180 === 0;
  const naturalWidth = pageSize && (upright ? pageSize.width : pageSize.height);
  const naturalHeight = pageSize && (upright ? pageSize.height : pageSize.width);
  const fitWidth = naturalWidth ? Math.min(naturalWidth, availableWidth) : undefined;

  // the space the page takes up follows the gesture continuously, while the canvas itself
  // only re-renders at the committed scale — so lifting a finger changes sharpness, not layout
  const displayWidth = fitWidth && fitWidth * liveScale;
  const displayHeight =
    fitWidth && naturalWidth && naturalHeight
      ? (fitWidth * naturalHeight * liveScale) / naturalWidth
      : undefined;

  return (
    <div className={s.viewer}>
      <div className={s.ScalingViewerControls}>
        <div>
          <button onClick={previousPage} disabled={pageNumber <= 1} className={s.PageButton}>
            <BiUndo />
          </button>
          <span>
            {pageNumber} of {numPages}
          </span>
          <button
            onClick={nextPage}
            disabled={pageNumber >= (numPages ?? 0)}
            className={s.PageButton}
          >
            <BiRedo />
          </button>
        </div>
        <div>
          <button onClick={zoomOut} className={s.ZoomButton}>
            <BiZoomOut />
          </button>
          <span className="text-sm font-medium min-w-16 text-center">
            {Math.round(liveScale * 100)}%
          </span>
          <button onClick={zoomIn} className={s.ZoomButton}>
            <BiZoomIn />
          </button>
          <button onClick={rotate} className={s.ZoomButton}>
            <BiRotateRight />
          </button>
        </div>
      </div>
      <div className={s.content} ref={pinchRef}>
        <div className={s.pdfPage} style={{ width: displayWidth, height: displayHeight }}>
          {/* the canvas renders at the committed scale, so an in-progress pinch is a
              plain transform on top of it instead of a canvas rebuild per touch event */}
          <div
            className={s.pdfCanvas}
            style={
              previewScale === 1
                ? undefined
                : { transform: `scale(${previewScale})`, transformOrigin: 'top left' }
            }
          >
            <Document file={storageLink} onLoadSuccess={onDocumentLoadSuccess}>
              <Page
                pageNumber={pageNumber}
                width={fitWidth}
                scale={scale}
                rotate={rotation}
                onLoadSuccess={onPageLoadSuccess}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>
          </div>
        </div>
      </div>
    </div>
  );
}
